import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { Form, Input, Space } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { DEFAULT_CODE, DEFAULT_REGION } from "@config";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { ISignProps } from "@models/home";
import ChangeLoginMode from "@pages/home/login/ChangeLoginMode";
import report from "@report";
import useInvalidShake from "@src/shared/resetpassword/useUnvalidShake";
import { updateSubmitLoading } from "@store/globalSlice";
import FormCaptchaItem from "../Form/CaptchaItem";
import PrefixSelector from "../Form/PrefixSelector";
import CommonButton from "@components/CommonButton";

export interface PhoneRef {
  setPhone: Function;
  phone: {
    validateStatus: "error" | "success" | "warning" | "validating";
    errorMsg: string;
  };
  getFieldsValue: Function;
  resetFields: Function;
}

const SMSWithCode = forwardRef<
  PhoneRef,
  {
    type: "login" | "signup" | "verify" | "addSubAccount" | "bingWxAccount";
    submit?: Function;
    checkISAgreeProtocol?: Function;
    preconditionFn?: Function | null | undefined;
  }
>(({ type, submit, checkISAgreeProtocol, preconditionFn }, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { submitLoading } = useAppSelector((state) => state.globalReducer);
  const captchaRef = useRef<any>(null);
  const [form] = Form.useForm();
  const [phoneStatus, setPhoneStatus] = useState<{
    validateStatus: "error" | "success" | "warning" | "validating";
    errorMsg: string;
  }>({
    validateStatus: "success",
    errorMsg: "",
  });
  let { userTelephone } = useAppSelector((state) => state.configReducer);
  const [phonePrefix, setPhonePrefix] = useState(DEFAULT_CODE() || "86");
  const [phone, setPhone] = useState<string>(localStorage.getItem("phone") || "");
  const { shakeClassName: _shakeClassName, handleInvalidShake: _handleInvalidShake } = useInvalidShake();

  const validatePhone = (value: string, isSubmit?: boolean) => {
    if (!value && isSubmit !== true) return;
    localStorage.setItem("phone", form.getFieldValue("phone"));
    const userTelephoneOption = userTelephone.find((item) => item.code === form.getFieldValue("prefix"));
    const phoneRule = userTelephoneOption?.phoneRule || "";
    if (value && new RegExp(phoneRule)?.test(value)) {
      setPhoneStatus({
        validateStatus: "success",
        errorMsg: "",
      });
      return true;
    } else {
      setPhoneStatus({
        validateStatus: "error",
        errorMsg: "Enter a valid phone number",
      });
      return false;
    }
  };

  const onFinish = async (value: ISignProps) => {
    let res = validatePhone(form.getFieldValue("phone"), true);
    if (!res) return;
    // 验证码为空时，禁止提交
    const captcha = form.getFieldValue("captcha");
    if (!captcha || !captcha.trim()) {
      captchaRef.current?.validateCaptchaError(t("Please enter verification code"));
      return;
    }
    dispatch(updateSubmitLoading(true));

    // 提交时先校验验证码正确提交 result
    if (validatePhone(form.getFieldValue("phone"))) {
      const nextFn = () =>
        submit?.(
          {
            ...value,
            smsType: "phone",
          },
          "SMS_" + type,
          (err: string) => {
            dispatch(updateSubmitLoading(false));
            captchaRef.current.validateCaptchaError(err);
            // 提交报错
            report.event("code_check_fail", {
              kalo_value: "+" + form.getFieldValue("prefix") + " " + form.getFieldValue("phone"),
              kalo_type: "sms_" + type,
            });
          },
        );
      if (checkISAgreeProtocol && !checkISAgreeProtocol?.(nextFn)) return dispatch(updateSubmitLoading(false));
      nextFn();
    }
  };

  let prefix = Form.useWatch("prefix", form) || "";

  const onCodeChange = () => {
    prefix = form.getFieldValue("prefix");
    const phoneRegion = userTelephone.find((el: any) => el.code === prefix)?.value || DEFAULT_REGION;
    localStorage.setItem("phoneRegion", phoneRegion);
    setPhonePrefix(prefix);
  };

  useEffect(() => {
    const captcha_phone = sessionStorage.getItem("captcha_phone") || "";
    captcha_phone && form.setFieldValue("phone", captcha_phone);
  }, []);

  useImperativeHandle(ref, () => ({
    setPhone: setPhoneStatus,
    captchaRef: captchaRef.current,
    phone: phoneStatus,
    getFieldsValue: form.getFieldsValue,
    resetFields: form.resetFields,
  }));

  // 切换区号、切换语言触发校验、触发手机号校验
  useEffect(() => {
    localStorage.setItem("phonePrefix", phonePrefix);
    validatePhone(form.getFieldValue("phone"));
  }, [phonePrefix]);

  // 组件挂载时设置表单初始值，确保手机号回显
  useEffect(() => {
    const savedPhone = localStorage.getItem("phone") || "";
    if (savedPhone) {
      form.setFieldsValue({ phone: savedPhone });
      setPhone(savedPhone);
    }
  }, []);

  return (
    <div>
      <div className="Component-SmsWithCode">
        <div className="Component-Form" key="phone-code">
          <Form
            form={form}
            name="register"
            onFinish={onFinish}
            initialValues={{ prefix: phonePrefix, phone, captcha: "" }}
            scrollToFirstError
          >
            <Form.Item validateStatus={phoneStatus.validateStatus} help={t(phoneStatus.errorMsg)} name="phone">
              <Space.Compact style={{ width: "100%" }}>
                <PrefixSelector
                  onChange={onCodeChange}
                  userTelephone={userTelephone}
                  className="h-[50px] text-[14px]! rounded-l-[4px]!"
                />
                <Input
                  size="large"
                  placeholder={t("Phone Number")}
                  className="h-[50px] text-[14px]! rounded-r-[4px]! border-l-0!"
                  value={phone}
                  onChange={(values) => {
                    const phone = values.target.value || "";
                    form.setFieldsValue({ phone });
                    validatePhone(phone);
                    setPhone(phone);
                  }}
                  onFocus={() => {
                    report.event("input", {
                      kalo_pos: "phone-input-focus",
                      kalo_value: "+" + form.getFieldValue("prefix") + " " + form.getFieldValue("phone"),
                      kalo_type: type,
                    });
                  }}
                  autoFocus
                />
              </Space.Compact>
            </Form.Item>

            <FormCaptchaItem
              ref={captchaRef}
              {...({ phone: phoneStatus } as any)}
              form={form}
              preconditionFn={preconditionFn}
              type={type === "bingWxAccount" ? "signup" : type}
              codeType="phone"
              setCaptchaValue={(_value: any) => {}}
              isAliyunCaptcha={phonePrefix === "86"}
            />

            {/* 登录保持不变 */}
            {(type === "login" || type === "signup") && (
              <Form.Item>
                <CommonButton
                  loading={submitLoading}
                  className="w-full h-[50px] text-[16px] rounded-[4px]! mt-[40px]"
                  type="primary"
                >
                  <Trans>{type === "login" ? "Log in" : "Sign up"}</Trans>
                </CommonButton>
              </Form.Item>
            )}
            {type === "bingWxAccount" && (
              <Form.Item>
                <CommonButton
                  loading={submitLoading}
                  className="w-full h-[50px] text-[16px] rounded-[4px]! mt-[40px]"
                  type="primary"
                >
                  <Trans>Confirm</Trans>
                </CommonButton>
              </Form.Item>
            )}
          </Form>
        </div>
      </div>
      {type === "login" && (
        <div className="flex flex-row items-center justify-between">
          <ChangeLoginMode />
          {/* <ForgetPassword
            type="phone"
            phonePrefix={phonePrefix}
            phone={phone}
            handleValid={() => {
              const valid = Boolean(validatePhone(phone, true));
              handleInvalidShake(valid);
              return valid;
            }}
          ></ForgetPassword> */}
        </div>
      )}
    </div>
  );
});

export default SMSWithCode;
